import { useMemo } from 'react';

/**
 * Hook personnalisé pour générer les styles de React Select
 * avec prise en charge du mode sombre et des variables CSS
 * @param {Object} customStyles - Styles personnalisés optionnels pour écraser les valeurs par défaut
 * @returns {Object} Styles configurés pour React Select
 */
export const useSelectStyles = (customStyles = {}) => {
  return useMemo(() => {
    const isDarkMode = document.documentElement.classList.contains('dark');

    // Base design: align with FiltersPanel (CSS vars with light/dark fallbacks)
    const baseStyles = {
      control: (provided) => ({
        ...provided,
        minHeight: '36px',
        height: 'auto',
        backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)',
        borderColor: isDarkMode ? 'var(--select-border, #475569)' : 'var(--select-border, #d1d5db)',
        '&:hover': {
          borderColor: isDarkMode ? 'var(--select-border-hover, #64748b)' : 'var(--select-border-hover, #9ca3af)'
        }
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)',
        border: `1px solid ${isDarkMode ? 'var(--select-border, #475569)' : 'var(--select-border, #d1d5db)'}`,
        zIndex: 999
      }),
      menuList: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)'
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused
          ? (isDarkMode ? 'var(--select-option-hover-bg, #334155)' : 'var(--select-option-hover-bg, #f3f4f6)')
          : (isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)'),
        color: isDarkMode ? 'var(--select-text, #e2e8f0)' : 'var(--select-text, #111827)'
      }),
      singleValue: (provided) => ({
        ...provided,
        color: isDarkMode ? 'var(--select-text, #e2e8f0)' : 'var(--select-text, #111827)'
      }),
      input: (provided) => ({
        ...provided,
        margin: 0,
        color: isDarkMode ? 'var(--select-text, #e2e8f0)' : 'var(--select-text, #111827)'
      }),
      valueContainer: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)'
      }),
      placeholder: (provided) => ({
        ...provided,
        color: isDarkMode ? 'var(--select-placeholder, #64748b)' : 'var(--select-placeholder, #6b7280)'
      })
    };

    // Merge custom overrides
    const mergedStyles = { ...baseStyles };
    Object.keys(customStyles).forEach(key => {
      if (typeof customStyles[key] === 'function' && typeof baseStyles[key] === 'function') {
        mergedStyles[key] = (...args) => {
          const baseResult = baseStyles[key](...args);
          const customResult = customStyles[key](...args);
          return { ...baseResult, ...customResult };
        };
      } else {
        mergedStyles[key] = customStyles[key];
      }
    });

    return mergedStyles;
  }, [customStyles]);
};

/**
 * Non-hook version with same design system as above
 * @param {boolean} isDarkMode
 * @param {Object} customStyles
 * @returns {Object}
 */
export const getSelectStyles = (isDarkMode = false, customStyles = {}) => {
  const baseStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '36px',
      height: 'auto',
      backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)',
      borderColor: isDarkMode ? 'var(--select-border, #475569)' : 'var(--select-border, #d1d5db)',
      '&:hover': {
        borderColor: isDarkMode ? 'var(--select-border-hover, #64748b)' : 'var(--select-border-hover, #9ca3af)'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)',
      border: `1px solid ${isDarkMode ? 'var(--select-border, #475569)' : 'var(--select-border, #d1d5db)'}`,
      zIndex: 999
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? (isDarkMode ? 'var(--select-option-hover-bg, #334155)' : 'var(--select-option-hover-bg, #f3f4f6)')
        : (isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)'),
      color: isDarkMode ? 'var(--select-text, #e2e8f0)' : 'var(--select-text, #111827)'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? 'var(--select-text, #e2e8f0)' : 'var(--select-text, #111827)'
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      color: isDarkMode ? 'var(--select-text, #e2e8f0)' : 'var(--select-text, #111827)'
    }),
    valueContainer: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? 'var(--select-bg, #1e293b)' : 'var(--select-bg, #ffffff)'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? 'var(--select-placeholder, #64748b)' : 'var(--select-placeholder, #6b7280)'
    })
  };

  const mergedStyles = { ...baseStyles };
  Object.keys(customStyles).forEach(key => {
    if (typeof customStyles[key] === 'function' && typeof baseStyles[key] === 'function') {
      mergedStyles[key] = (...args) => {
        const baseResult = baseStyles[key](...args);
        const customResult = customStyles[key](...args);
        return { ...baseResult, ...customResult };
      };
    } else {
      mergedStyles[key] = customStyles[key];
    }
  });

  return mergedStyles;
};